import React, { useEffect, useMemo, useState } from 'react';
import { Grid, Icon, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Card } from '@totalsoft/rocket-ui';
import PropTypes from 'prop-types';
import { get, over, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { useMutation } from 'hooks/apollo/useMutation';
import { ADD_OR_UPDATE_DOCUMENT_SYNC_STATE, DELETE_DOCUMENT_SYNC_STATE } from '../mutations';
import { DOCUMENT_SYNC_STATE_DELETED, DOCUMENT_SYNC_STATE_UPDATED } from '../subscriptions';
import useSubscription from 'hooks/apollo/useSubscription';
import { useToast } from 'hooks/toasts/useToast';
import { required, shape } from '@totalsoft/pure-validations';
import { useDirtyFieldValidation, isValid, getErrors } from '@totalsoft/pure-validations-react';
import IconButton from 'components/common/buttons/IconButton';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import { documentSyncStatesStore } from './store';

const validator = shape({
  destinationDocumentTypeCode: required,
  destinationFinalStateCode: required,
  documentSyncTypeId: required,
  sourceDocumentTypeCode: required,
  sourceStateCode: required
});

const ItemComponent = ({ data, updateValueRef, documentStatesXType, documentTypes, dictionarySyncTypes, onDelete, existingIds }) => {
  const { t } = useTranslation();
  const addToast = useToast();
  const confirm = useConfirmDialog();

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens(data);

  const [validation, validate] = useDirtyFieldValidation(validator);

  const [pending, setPending] = useState(false);

  useEffect(() => {
    resetLens(data);
  }, [resetLens, data]);

  useEffect(() => {
    validate(lens |> get, dirtyInfo);
  }, [validate, dirtyInfo, lens]);

  // #region Handlers

  const handlePropertyUpdate = property => value => {
    (lens[property] |> set)(value);
    updateValueRef && updateValueRef(data.documentSyncStateId, { [property]: value });
  };

  const handleSourceTypeUpdate = value => {
    over(lens, prev => ({
      ...prev,
      sourceDocumentTypeCode: value,
      sourceStateCode: null,
      destinationDocumentTypeCode: null
    }));
    updateValueRef &&
      updateValueRef(data.documentSyncStateId, { sourceDocumentTypeCode: value, sourceStateCode: null, destinationDocumentTypeCode: null });
  };

  const handleDestinationTypeUpdate = value => {
    over(lens, prev => ({
      ...prev,
      destinationDocumentTypeCode: value,
      destinationFinalStateCode: null,
      destinationInitialStateCode: null
    }));
    updateValueRef &&
      updateValueRef(data.documentSyncStateId, {
        destinationDocumentTypeCode: value,
        destinationFinalStateCode: null,
        destinationInitialStateCode: null
      });
  };

  // #endregion

  // #region Collections

  const documentSourceStatesXType = useMemo(
    () => documentStatesXType.filter(state => state.documentTypeCode === (lens.sourceDocumentTypeCode |> get)),
    [lens, documentStatesXType]
  );

  const documentDestinationStateXType = useMemo(
    () => documentStatesXType.filter(state => state.documentTypeCode === (lens.destinationDocumentTypeCode |> get)),
    [lens, documentStatesXType]
  );

  const destinationDocumentType = useMemo(
    () =>
      documentTypes.filter(state =>
        (lens.documentSyncTypeId |> get) === 2
          ? !state.isExternal && state.code !== (lens.sourceDocumentTypeCode |> get)
          : state.code !== (lens.sourceDocumentTypeCode |> get)
      ),
    [lens, documentTypes]
  );

  // #endregion

  // #region Save

  const [saveDocumentSyncState] = useMutation(ADD_OR_UPDATE_DOCUMENT_SYNC_STATE);

  const handleSave = () => {
    const data = lens |> get;
    if (!validate(data)) return;
    setPending(true);
    saveDocumentSyncState({ variables: { input: data } });
  };

  useSubscription(DOCUMENT_SYNC_STATE_UPDATED, {
    variables: { id: data.documentSyncStateId },
    onData: ({ data }) => {
      if (!data.documentSyncStateId && existingIds) {
        const updatedId = data.data.documentSyncStateUpdated.documentSyncState.documentSyncStateId;
        if (existingIds.includes(updatedId)) return;
      }
      setPending(false);
      addToast(t('DocumentSyncState.DocumentSyncStateUpdated'), 'success');
      documentSyncStatesStore.get().refetch();
      if (!data.documentSyncStateId && onDelete) onDelete();
    }
  });

  // #endregion

  // #region Delete

  const [deleteDocumentSyncState] = useMutation(DELETE_DOCUMENT_SYNC_STATE);

  const handleDelete = () => {
    const documentSyncStateId = lens.documentSyncStateId |> get;
    confirm(
      () => {
        if (onDelete) onDelete();
        else {
          setPending(true);
          deleteDocumentSyncState({ variables: { input: { documentSyncStateId } } });
        }
      },
      {
        content: t('DocumentSyncState.DeleteItem')
      }
    );
  };

  useSubscription(DOCUMENT_SYNC_STATE_DELETED, {
    variables: { id: data.documentSyncStateId },
    skip: !data.documentSyncStateId,
    onData: () => {
      setPending(false);
      addToast(t('DocumentSyncState.DocumentSyncStateRemoved'), 'success');
      documentSyncStatesStore.get().refetch();
    }
  });

  // #endregion

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  return (
    <Card>
      <Grid container spacing={2}>
        <Grid item xs={12} display='flex' justifyContent='end' gap={0.5} alignItems='center'>
          {!data.documentSyncStateId && (
            <Tooltip title={t('New')}>
              <Icon color={pending ? 'disabled' : 'info'} sx={{ mr: 0.75, cursor: 'pointer' }}>
                info
              </Icon>
            </Tooltip>
          )}
          <IconButton type='save' disabled={!isDirty || pending} onClick={handleSave} />
          <IconButton type='delete' color='error' disabled={pending} onClick={handleDelete} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            fullWidth
            simpleValue
            value={lens.sourceDocumentTypeCode |> get}
            onChange={handleSourceTypeUpdate}
            options={documentTypes}
            label={t('DocumentSyncState.SourceDocumentType')}
            valueKey='code'
            error={!isValid(validation.sourceDocumentTypeCode)}
            helperText={getErrors(validation.sourceDocumentTypeCode)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            fullWidth
            simpleValue
            value={lens.sourceStateCode |> get}
            onChange={handlePropertyUpdate('sourceStateCode')}
            options={documentSourceStatesXType}
            label={t('DocumentSyncState.SourceState')}
            valueKey='documentStateCode'
            labelKey='documentStateName'
            isSearchable
            error={!isValid(validation.sourceStateCode)}
            helperText={getErrors(validation.sourceStateCode)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            fullWidth
            simpleValue
            value={lens.documentSyncTypeId |> get}
            onChange={handlePropertyUpdate('documentSyncTypeId')}
            options={dictionarySyncTypes}
            label={t('DocumentSyncState.ActionType')}
            error={!isValid(validation.documentSyncTypeId)}
            helperText={getErrors(validation.documentSyncTypeId)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            fullWidth
            simpleValue
            value={lens.destinationDocumentTypeCode |> get}
            onChange={handleDestinationTypeUpdate}
            options={destinationDocumentType}
            label={t('DocumentSyncState.DestinationDocumentType')}
            valueKey='code'
            error={!isValid(validation.destinationDocumentTypeCode)}
            helperText={getErrors(validation.destinationDocumentTypeCode)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            fullWidth
            simpleValue
            value={lens.destinationInitialStateCode |> get}
            onChange={handlePropertyUpdate('destinationInitialStateCode')}
            options={documentDestinationStateXType}
            label={t('DocumentSyncState.DestinationInitialState')}
            valueKey='documentStateCode'
            labelKey='documentStateName'
            isClearable
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            fullWidth
            simpleValue
            value={lens.destinationFinalStateCode |> get}
            onChange={handlePropertyUpdate('destinationFinalStateCode')}
            options={documentDestinationStateXType}
            label={t('DocumentSyncState.DestinationFinalState')}
            valueKey='documentStateCode'
            labelKey='documentStateName'
            error={!isValid(validation.destinationFinalStateCode)}
            helperText={getErrors(validation.destinationFinalStateCode)}
          />
        </Grid>
      </Grid>
    </Card>
  );
};

ItemComponent.propTypes = {
  data: PropTypes.object,
  updateValueRef: PropTypes.func,
  documentStatesXType: PropTypes.array,
  documentTypes: PropTypes.array,
  dictionarySyncTypes: PropTypes.array,
  onDelete: PropTypes.func,
  existingIds: PropTypes.array
};

export default ItemComponent;
